import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { getPatients } from "../../../api/services/api";

export type PatientOption = {
  id: number;
  users?: {
    fullName?: string;
    full_name?: string;
    email?: string;
    mobileNumber?: string;
    mobile_number?: string;
  };
  fullName?: string;
  full_name?: string;
  email?: string;
};

const getPatientName = (p: PatientOption) =>
  p?.users?.fullName ||
  p?.users?.full_name ||
  p?.fullName ||
  p?.full_name ||
  `Patient #${p?.id}`;

const getPatientEmail = (p: PatientOption) =>
  p?.users?.email || p?.email || "";

const getPatientPhone = (p: PatientOption) =>
  p?.users?.mobileNumber || p?.users?.mobile_number || "";

export const patientOptionLabel = (p: PatientOption) => {
  const name = getPatientName(p);
  const email = getPatientEmail(p);
  const phone = getPatientPhone(p);
  const parts = [name];
  if (email) parts.push(email);
  if (phone) parts.push(phone);
  parts.push(`ID ${p.id}`);
  return parts.join(" · ");
};

type SharedProps = {
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  helperText?: string;
  minSearchLength?: number;
  limit?: number;
};

type SingleProps = SharedProps & {
  multiple?: false;
  value: PatientOption | null;
  onChange: (value: PatientOption | null) => void;
};

type MultipleProps = SharedProps & {
  multiple: true;
  value: PatientOption[];
  onChange: (value: PatientOption[]) => void;
};

export type PatientSearchAutocompleteProps = SingleProps | MultipleProps;

/**
 * Debounced patient typeahead against GET /patients?search=
 * (name, email, mobile, patient id on the server).
 */
export const PatientSearchAutocomplete = (
  props: PatientSearchAutocompleteProps
) => {
  const {
    label = "Search patients",
    placeholder = "Name, email, phone, or ID…",
    disabled,
    helperText,
    minSearchLength = 1,
    limit = 25,
  } = props;

  const [inputValue, setInputValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(inputValue.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [inputValue]);

  const canSearch = debouncedSearch.length >= minSearchLength;

  const { data, isFetching } = useQuery(
    ["patients-search", { search: debouncedSearch, limit, offset: 0 }],
    getPatients,
    {
      enabled: canSearch,
      keepPreviousData: true,
    }
  );

  const options: PatientOption[] =
    data?.data?.patients || data?.data?.data || [];

  const commonAutocompleteProps = {
    options,
    filterOptions: (opts: PatientOption[]) => opts,
    getOptionLabel: patientOptionLabel,
    isOptionEqualToValue: (opt: PatientOption, val: PatientOption) =>
      opt.id === val.id,
    loading: isFetching,
    disabled,
    inputValue,
    onInputChange: (_e: React.SyntheticEvent, value: string) => {
      setInputValue(value);
    },
    noOptionsText: canSearch
      ? isFetching
        ? "Searching…"
        : "No patients found"
      : `Type at least ${minSearchLength} character(s)`,
    renderOption: (
      liProps: React.HTMLAttributes<HTMLLIElement>,
      option: PatientOption
    ) => {
      const name = getPatientName(option);
      const email = getPatientEmail(option);
      const phone = getPatientPhone(option);
      return (
        <li {...liProps} key={option.id}>
          <Box sx={{ py: 0.5 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {name}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              {[email, phone, `ID ${option.id}`].filter(Boolean).join(" · ")}
            </Typography>
          </Box>
        </li>
      );
    },
  };

  if (props.multiple) {
    return (
      <Autocomplete
        {...commonAutocompleteProps}
        multiple
        filterSelectedOptions
        value={props.value}
        onChange={(_e, value) => props.onChange(value)}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="standard"
            label={label}
            placeholder={placeholder}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isFetching ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    );
  }

  return (
    <Autocomplete
      {...commonAutocompleteProps}
      value={props.value}
      onChange={(_e, value) => props.onChange(value)}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          size="small"
          margin="dense"
          fullWidth
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          required
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
